import { TextField, FormHelperText, Button } from '@material-ui/core';
import { map } from 'rxjs/operators';
import React, { useCallback, useRef } from 'react';
import { Subscription } from 'rxjs';
import { Autocomplete } from '@material-ui/lab';
import { debounce } from 'lodash';
import MovieCreationIcon from '@material-ui/icons/MovieCreation';
import OfflinePinIcon from '@material-ui/icons/OfflinePin';
import * as personService from '@api/person';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useHistory } from 'react-router-dom';
import { Grid, Chip, Link, Avatar } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import PersonDefaultIcon from '@icon/person-default';

import { Link as Link1 } from 'react-router-dom';

export function PersonSearch({
  associatedPerson,
  setAssociatedPerson,
  multiple = false,
  label = 'Associated Person',
  freeSolo = true,
  disabled = false
}) {
  const classesForAvatarImage = useStyles();
  const lastSubscription = useRef<Subscription>(null);
  const lastSearchedvalue = useRef<string>(null);

  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [personCount, setPersonCount] = React.useState(null);

  const onChangeHandle = useCallback(value => {
    if (lastSearchedvalue.current && lastSearchedvalue.current == value && options.length > 0) {
      setOptions(prevOptions => [...prevOptions]);
      return;
    }
    setOptions([]);
    lastSearchedvalue.current = value;
    if (lastSubscription.current) {
      lastSubscription.current.unsubscribe();
    }
    setLoading(true);
    lastSubscription.current = personService
      .personSearch({
        searchKey: 'name',
        query: value,
      })
      .pipe(
        map(res => ({
          ...res.data,
          data: res.data.data.map(d => {
            const reqdData = { ...d, id: d.vtr_id, person_vtr_id: d.vtr_id };
            delete reqdData.vtr_id;
            return reqdData;
          }),
        }))
      )
      .subscribe({
        next: data => {
          setOptions(data.data);
          setPersonCount(data.count);
        },
        error: data => {
          console.error(data, 'Error');
        },
        complete: () => {
          setLoading(false);
        },
      });
  }, []);
  const debouncedOnChangeHandle = useCallback(
    debounce(value => onChangeHandle(value), 2000),
    [onChangeHandle]
  );

  // Ensure value format matches multiple prop
  const safeValue = React.useMemo(() => {
    if (!associatedPerson) {
      return multiple ? [] : null;
    }
    
    if (multiple) {
      // For multiple mode, ensure we have an array
      return Array.isArray(associatedPerson) ? associatedPerson : [associatedPerson];
    } else {
      // For single mode, ensure we have a single value
      return Array.isArray(associatedPerson) ? (associatedPerson[0] || null) : associatedPerson;
    }
  }, [associatedPerson, multiple]);

  // Ensure options is always an array
  const safeOptions = React.useMemo(() => {
    return Array.isArray(options) ? options : [];
  }, [options]);

  return (
    <>
      <Autocomplete
        fullWidth
        freeSolo={freeSolo}
        multiple={multiple}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        disableClearable
        getOptionLabel={option => (typeof option === 'object' ? (option?.name || '') : (option || ''))}
        options={safeOptions}
        disabled={disabled}
        renderTags={(tagValue, getTagProps) => {
          // Safety check to ensure tagValue is an array
          if (!Array.isArray(tagValue)) {
            return [];
          }
          return tagValue.map((option, index) => (
            <Chip
              label={typeof option === 'object' ? option.name : option}
              icon={
                typeof option === 'object' && option.person_vtr_id ? (
                  <OfflinePinIcon style={{ fontSize: '1.5em' }} color="primary" />
                ) : null
              }
              {...getTagProps({ index })}
              color="secondary"
            />
          ));
        }}
        renderOption={option => (
          <Grid container alignItems="center">
            <Grid item xs={1}>
              <Avatar
                className={classesForAvatarImage.medium}
                alt={option.name}
                src={`https://image.tmdb.org/t/p/w92${option.profile_path}`}>
                <PersonDefaultIcon />
              </Avatar>
            </Grid>
            <Grid item xs={3}>
              {option.name}
            </Grid>
            <Grid item xs={2}>
              {option.role_id}
            </Grid>
            {option.imdb_id && (
              <Grid item xs={3}>
                <>
                  &nbsp;
                  <Chip
                    color="primary"
                    size="small"
                    icon={<MovieCreationIcon fontSize="inherit" />}
                    component={Link}
                    href={`https://www.imdb.com/name/${option.imdb_id}`}
                    target="_blank"
                    label="IMDB"
                  />
                </>
              </Grid>
            )}

            {option.person_tmdb_id && (
              <Grid item xs={3}>
                <>
                  &nbsp;
                  <Chip
                    size="small"
                    color="secondary"
                    label="TMDB"
                    icon={<MovieCreationIcon fontSize="inherit" />}
                    component={Link}
                    href={`https://www.themoviedb.org/person/${option.person_tmdb_id}`}
                    target="_blank"
                  />
                </>
              </Grid>
            )}
          </Grid>
        )}
        loading={loading}
        loadingText={
          'Searching ... '
          // + (typeof value === 'object' ? value.name : value) + '...'
        }
        value={safeValue}
        onChange={(evt, v) => setAssociatedPerson(v)}
        onInputChange={(evt, v) => {
          if (freeSolo) {
            if (!multiple) {
              if (associatedPerson) {
                if (associatedPerson.name !== v) setAssociatedPerson(v);
              } else {
                setAssociatedPerson(v);
              }
            }
          }
        }}
        renderInput={props => (
          <TextField
            // helperText="Select associated person relevant to this deal."
            label={label}
            placeholder={label}
            fullWidth
            variant="outlined"
            {...props}
            onChange={ev => {
              if (!!ev.target.value) {
                debouncedOnChangeHandle(ev.target.value);
              }
            }}
            onFocus={ev => {
              if (!!ev.target.value) {
                debouncedOnChangeHandle(ev.target.value);
              }
            }}
            InputProps={{
              ...props.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? (
                    <>
                      <CircularProgress color="inherit" size={20} />
                      {props.InputProps.endAdornment}
                    </>
                  ) : associatedPerson && associatedPerson.person_vtr_id ? (
                    <>
                      {props.InputProps.endAdornment}
                      <OfflinePinIcon style={{ fontSize: '1.5em' }} color="primary" />
                    </>
                  ) : null}
                </React.Fragment>
              ),
            }}
          />
        )}
      />

      <div className={personCount == 0 ? classesForAvatarImage.personStatus : ''}>
        <PersonStatus personData={personCount} />
      </div>
    </>
  );
}

export function PersonStatus(props) {
  let history = useHistory();
  // const navigateNew = () => {
  //   history.push('https://stage-deli.vitrina.ai/person/new');
  // };
  const { personData } = props;
  if (personData == 0) {
    return (
      <FormHelperText>
        No person found
        {/* <Link1 to="/person-management/new">Create New</Link1> */}
      </FormHelperText>
    );
  }
  return <FormHelperText>Select the Associated Person</FormHelperText>;
}
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      '& > *': {
        margin: theme.spacing(1),
      },
    },
    personStatus: {
      marginTop: theme.spacing(8),
    },
    small: {
      width: theme.spacing(3),
      height: theme.spacing(3),
    },
    medium: {
      width: theme.spacing(4),
      height: theme.spacing(4),
    },
    large: {
      width: theme.spacing(7),
      height: theme.spacing(7),
    },
  })
);
